
import React, { useState, useEffect } from 'react';
import {
    collection,
    addDoc,
    setDoc,
    doc,
    serverTimestamp,
    getDocs,
    query,
    where
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, secondaryAuth } from '../services/firebase';
import { locationsByState, talukasByDistrict } from '../constants/locations';
import LoadingSpinner from '../components/LoadingSpinner';
import { useNavigate } from 'react-router-dom';

const SuperAdminFranchiseManagement = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [franchises, setFranchises] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        ownerName: '',
        email: '',
        phone: '',
        password: '',
        city: '',
        state: '',
        district: '',
        franchiseTaluka: '',
        // For Access Control
        accessState: '',
        accessDistrict: '',
        accessTaluka: '',
        assignedAreas: [], // Array of {state, district, taluka}

        validityStart: '',
        validityEnd: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Fetch franchises on mount
    useEffect(() => {
        fetchFranchises();
    }, []);

    const fetchFranchises = async () => {
        try {
            const q = query(collection(db, 'franchises'));
            const querySnapshot = await getDocs(q);
            const franchiseList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setFranchises(franchiseList);
        } catch (err) {
            console.error("Error fetching franchises:", err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleStateChange = (e) => {
        const newState = e.target.value;
        const { name } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: newState,
            [name === 'state' ? 'district' : 'accessDistrict']: '',
            [name === 'state' ? 'franchiseTaluka' : 'accessTaluka']: '',
        }));
    };

    const handleDistrictChange = (e) => {
        const newDistrict = e.target.value;
        const { name } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: newDistrict,
            [name === 'district' ? 'franchiseTaluka' : 'accessTaluka']: '',
        }));
    };

    const handleAddAccessArea = () => {
        const { state, accessDistrict, assignedAreas } = formData;

        // Use main state for access control
        const accessState = state;

        if (!accessState || !accessDistrict) return;

        // Check for duplicates
        const exists = assignedAreas.some(area =>
            area.state === accessState &&
            area.district === accessDistrict
        );

        if (!exists) {
            setFormData(prev => ({
                ...prev,
                assignedAreas: [...prev.assignedAreas, {
                    state: accessState,
                    district: accessDistrict,
                    taluka: 'All' // Full district access
                }],
                // Reset selection after adding
                accessDistrict: ''
            }));
        }
    };

    const handleRemoveAccessArea = (index) => {
        setFormData(prev => ({
            ...prev,
            assignedAreas: prev.assignedAreas.filter((_, i) => i !== index)
        }));
    };

    const validateForm = () => {
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            return "Invalid email address format / अवैध ईमेल पत्ता";
        }

        // Phone validation
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(formData.phone)) {
            return "Phone number must be exactly 10 digits / फोन नंबर 10 अंकी असावा";
        }

        // Password validation
        if (formData.password.length < 6) {
            return "Password must be at least 6 characters long";
        }

        // Assignments validation
        if (formData.assignedAreas.length === 0) {
            return "Please assign at least one territory";
        }

        // Date validation
        if (formData.validityStart && formData.validityEnd) {
            const start = new Date(formData.validityStart);
            const end = new Date(formData.validityEnd);
            if (end <= start) {
                return "End date must be after start date";
            }
        }

        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            setLoading(false);
            return;
        }

        try {
            // 1. Create User in Firebase Auth (using secondary app to avoid logging out admin)
            // Password is set by super admin
            const userCredential = await createUserWithEmailAndPassword(
                secondaryAuth,
                formData.email,
                formData.password
            );
            const uid = userCredential.user.uid;

            // 2. Save role in users/{uid}
            await setDoc(doc(db, 'users', uid), {
                email: formData.email,
                phone: formData.phone,
                userType: 'franchise',
                createdAt: serverTimestamp(),
                // Add other relevant user fields if necessary
            });

            // 3. Add Franchise DB structure under franchises/{franchiseId}
            // We use the UID as the franchiseId for easy mapping
            const servedDistricts = formData.assignedAreas.map(area => `${area.state}_${area.district}`);

            const franchiseData = {
                name: formData.name,
                ownerName: formData.ownerName,
                email: formData.email,
                phone: formData.phone,
                city: formData.city,
                state: formData.state,
                district: formData.district,
                taluka: formData.franchiseTaluka, // Physical location

                // Operational Areas
                assignedAreas: formData.assignedAreas,
                servicedDistricts: servedDistricts, // For easier querying

                isLimitedQuota: false,
                validityStart: formData.validityStart,
                validityEnd: formData.validityEnd,
                status: 'active', // Default status
                franchiseId: uid, // storing ID explicitly as well
                createdAt: serverTimestamp()
            };

            await setDoc(doc(db, 'franchises', uid), franchiseData);

            // Reset form and refresh list
            setSuccess('Franchise created successfully!');
            setFormData({
                name: '',
                ownerName: '',
                email: '',
                phone: '',
                password: '',
                city: '',
                state: '',
                district: '',
                franchiseTaluka: '',
                accessState: '',
                accessDistrict: '',
                accessTaluka: '',
                assignedAreas: [],
                validityStart: '',
                validityEnd: ''
            });
            setShowModal(false);
            fetchFranchises();

            // Sign out from secondary app just in case
            await secondaryAuth.signOut();

        } catch (err) {
            console.error("Error creating franchise:", err);
            // Handle "email already in use" etc.
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Franchise Management</h1>
                <button
                    onClick={() => setShowModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Add New Franchise
                </button>
            </div>

            {loading && <LoadingSpinner />}

            {/* Franchise List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Franchise Name</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Owner</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">District & State</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Assigned Territories</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Validity</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {franchises.length > 0 ? (
                                franchises.map((franchise) => (
                                    <tr key={franchise.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{franchise.name}</div>
                                            <div className="text-sm text-gray-500">{franchise.email}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900">{franchise.ownerName}</div>
                                            <div className="text-sm text-gray-500">{franchise.phone}</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {franchise.district ? `${franchise.district}, ` : ''}{franchise.state}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {franchise.assignedAreas && franchise.assignedAreas.length > 0
                                                ? franchise.assignedAreas.map(area => `${area.district} (All)`).join(', ')
                                                : franchise.assignedTalukas ? franchise.assignedTalukas.join(', ') : 'No Areas Assigned'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${franchise.status === 'active'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-red-100 text-red-700'
                                                }`}>
                                                {franchise.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {franchise.validityEnd}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                        No franchises found. Add one to get started.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Franchise Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800">Add New Franchise</h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {error && (
                                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}
                            {success && (
                                <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm">
                                    {success}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Franchise Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="ownerName"
                                        required
                                        value={formData.ownerName}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        required
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="9876543210"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="password"
                                        required
                                        minLength="6"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Min. 6 characters"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">This password will be used by franchise for login</p>
                                </div>
                            </div>

                            {/* Section 1: Franchise Address */}
                            <div className="border-t border-gray-100 pt-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">Franchise Address</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
                                        <select
                                            name="state"
                                            required
                                            value={formData.state}
                                            onChange={handleStateChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Select State</option>
                                            {Object.keys(locationsByState).map(state => (
                                                <option key={state} value={state}>{state}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">District <span className="text-red-500">*</span></label>
                                        <select
                                            name="district"
                                            required
                                            value={formData.district}
                                            onChange={handleDistrictChange}
                                            disabled={!formData.state}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        >
                                            <option value="">Select District</option>
                                            {formData.state && locationsByState[formData.state]?.map(district => (
                                                <option key={district} value={district}>{district}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Taluka <span className="text-red-500">*</span></label>
                                        <select
                                            name="franchiseTaluka"
                                            required
                                            value={formData.franchiseTaluka}
                                            onChange={handleChange}
                                            disabled={!formData.district}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        >
                                            <option value="">Select Taluka</option>
                                            {formData.district && talukasByDistrict[formData.district]?.map(taluka => (
                                                <option key={taluka} value={taluka}>{taluka}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">City / Village <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="city"
                                            required
                                            value={formData.city}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Operational Access */}
                            <div className="border-t border-gray-100 pt-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">Operational Access Control</h3>
                                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Add Territory Access</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">State: {formData.state || 'Selected above'}</label>
                                            <select
                                                name="accessDistrict"
                                                value={formData.accessDistrict}
                                                onChange={handleDistrictChange}
                                                disabled={!formData.state}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100"
                                            >
                                                <option value="">Select District</option>
                                                {formData.state && locationsByState[formData.state]?.map(district => (
                                                    <option key={district} value={district}>{district}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                type="button"
                                                onClick={handleAddAccessArea}
                                                disabled={!formData.accessDistrict}
                                                className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                            >
                                                Add District Access
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Assigned Territories List */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Territories <span className="text-red-500">*</span></label>
                                    {formData.assignedAreas.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {formData.assignedAreas.map((area, index) => (
                                                <span
                                                    key={index}
                                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                                                >
                                                    {area.district} (Entire District)
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveAccessArea(index)}
                                                        className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none"
                                                    >
                                                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-500 italic p-2 border border-dashed border-gray-300 rounded-lg text-center">
                                            No territories assigned yet. Add territories above.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-100 pt-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Validity Start <span className="text-red-500">*</span></label>
                                    <input
                                        type="date"
                                        name="validityStart"
                                        required
                                        value={formData.validityStart}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Validity End <span className="text-red-500">*</span></label>
                                    <input
                                        type="date"
                                        name="validityEnd"
                                        required
                                        value={formData.validityEnd}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Create Franchise
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuperAdminFranchiseManagement;
